// Canonical ArticleRenderer (§31) — used by BOTH admin preview and member
// detail. Never uses dangerouslySetInnerHTML. Renders the validated
// TipTap JSON via a strict whitelist walker (recursive function, not a
// recursive React component — Babel can trip on deeply nested self-
// referential JSX components).

import React from "react";
import "./ArticleRenderer.css";
// Self-hosted IBM Plex — no runtime fetch from fonts.googleapis.com/gstatic.
import "@fontsource/ibm-plex-serif/400.css";
import "@fontsource/ibm-plex-serif/400-italic.css";
import "@fontsource/ibm-plex-serif/700.css";
import "@fontsource/ibm-plex-sans/400.css";
import "@fontsource/ibm-plex-sans/500.css";
import "@fontsource/ibm-plex-sans/700.css";
import "@fontsource/ibm-plex-mono/400.css";
// Self-hosted JetBrains Mono — canonical article typeface (§7, macchina-da-scrivere aesthetic).
import "@fontsource/jetbrains-mono/400.css";
import "@fontsource/jetbrains-mono/400-italic.css";
import "@fontsource/jetbrains-mono/700.css";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const mediaUrl = (id) => `${BACKEND}/api/growing-circle/article-media/${id}`;

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });
  } catch { return iso; }
}

function applyMarks(text, marks) {
  let el = text;
  (marks || []).forEach((m, i) => {
    if (m.type === "bold") el = <strong key={`b${i}`}>{el}</strong>;
    else if (m.type === "italic") el = <em key={`i${i}`}>{el}</em>;
    else if (m.type === "underline") el = <u key={`u${i}`}>{el}</u>;
    else if (m.type === "link") {
      const href = m.attrs && m.attrs.href;
      if (typeof href === "string" && /^(https?:|mailto:)/.test(href)) {
        el = <a key={`l${i}`} href={href} target="_blank" rel="noopener noreferrer">{el}</a>;
      }
    }
  });
  return el;
}

function renderChildren(nodes) {
  const arr = Array.isArray(nodes) ? nodes : [];
  const out = [];
  for (let i = 0; i < arr.length; i++) {
    const r = renderNode(arr[i], i);
    if (r !== null) out.push(r);
  }
  return out;
}

function renderNode(node, key) {
  if (!node || typeof node !== "object") return null;
  const t = node.type;
  if (t === "doc") return <React.Fragment key={key}>{renderChildren(node.content)}</React.Fragment>;
  if (t === "paragraph") return <p key={key}>{renderChildren(node.content)}</p>;
  if (t === "heading") {
    const lvl = node.attrs && node.attrs.level;
    if (lvl === 2) return <h2 key={key}>{renderChildren(node.content)}</h2>;
    if (lvl === 3) return <h3 key={key}>{renderChildren(node.content)}</h3>;
    return null;
  }
  if (t === "text") {
    // Split raw text into consecutive runs, each carrying a stylistic
    // intent hint derived from Unicode Mathematical Alphanumeric blocks
    // (see splitStyledRuns). The run's text is already normalised to
    // ASCII — we then apply implicit bold/italic marks on top of any
    // marks the author explicitly set in TipTap.
    const runs = splitStyledRuns(node.text || "");
    return (
      <React.Fragment key={key}>
        {runs.map((run, i) => {
          const extra = [];
          if (run.key.includes("b")) extra.push({ type: "bold" });
          if (run.key.includes("i")) extra.push({ type: "italic" });
          const marks = extra.length ? [...(node.marks || []), ...extra] : (node.marks || []);
          return (
            <React.Fragment key={i}>{applyMarks(run.text, marks)}</React.Fragment>
          );
        })}
      </React.Fragment>
    );
  }
  if (t === "bulletList") return <ul key={key}>{renderChildren(node.content)}</ul>;
  if (t === "orderedList") return <ol key={key}>{renderChildren(node.content)}</ol>;
  if (t === "listItem") return <li key={key}>{renderChildren(node.content)}</li>;
  if (t === "image") {
    const mid = node.attrs && node.attrs.media_id;
    if (!mid) return null;
    const alt = (node.attrs && node.attrs.alt) || "";
    const decorative = !!(node.attrs && node.attrs.decorative);
    const caption = node.attrs && node.attrs.caption;
    return (
      <figure key={key}>
        <img src={mediaUrl(mid)} alt={decorative ? "" : alt} loading="lazy" />
        {caption ? <figcaption>{caption}</figcaption> : null}
      </figure>
    );
  }
  if (t === "hardBreak") return <br key={key} />;
  return null;
}

function collectText(node) {
  if (!node) return "";
  if (node.type === "text") return node.text || "";
  let s = "";
  for (const c of node.content || []) s += collectText(c);
  return s;
}

// Decode a single codepoint from the Unicode Mathematical Alphanumeric
// Symbols block (U+1D400–U+1D7FF) — and its handful of relatives that
// live outside the main block for historic reasons — into a plain ASCII
// character plus a hint about the stylistic intent the author used
// (bold, italic, or both).
//
// Users often paste text into TipTap from tools that emit these styled
// codepoints instead of applying real formatting marks (e.g. rich-text
// widgets, LaTeX exports, "fancy text" generators, some LLM outputs).
// JetBrains Mono covers only the ASCII range, so those characters fall
// back to a random system font (usually a serif italic) which breaks
// the uniform typewriter aesthetic of the article surface. Normalising
// them to real ASCII + real <em>/<strong> marks fixes the appearance
// AND makes the text searchable/selectable normally.
function decodeMathAlphanumeric(cp) {
  const R = (start, base, b, i) => (cp >= start && cp <= start + (base === 0x41 ? 25 : base === 0x61 ? 25 : 9))
    ? { ch: String.fromCharCode(base + (cp - start)), bold: b, italic: i } : null;
  return (
    // Bold — A-Z, a-z, 0-9
    R(0x1D400, 0x41, true,  false) ||
    R(0x1D41A, 0x61, true,  false) ||
    R(0x1D7CE, 0x30, true,  false) ||
    // Italic — A-Z, a-z (0x1D455 is a hole; U+210E carries italic 'h')
    R(0x1D434, 0x41, false, true) ||
    (cp === 0x210E ? { ch: "h", bold: false, italic: true } : null) ||
    (cp >= 0x1D44E && cp <= 0x1D454
      ? { ch: String.fromCharCode(0x61 + (cp - 0x1D44E)), bold: false, italic: true } : null) ||
    (cp >= 0x1D456 && cp <= 0x1D467
      ? { ch: String.fromCharCode(0x69 + (cp - 0x1D456)), bold: false, italic: true } : null) ||
    // Bold-italic — A-Z, a-z
    R(0x1D468, 0x41, true,  true) ||
    R(0x1D482, 0x61, true,  true) ||
    // Sans-serif regular — A-Z, a-z, 0-9
    R(0x1D5A0, 0x41, false, false) ||
    R(0x1D5BA, 0x61, false, false) ||
    R(0x1D7E2, 0x30, false, false) ||
    // Sans-serif bold — A-Z, a-z, 0-9
    R(0x1D5D4, 0x41, true,  false) ||
    R(0x1D5EE, 0x61, true,  false) ||
    R(0x1D7EC, 0x30, true,  false) ||
    // Sans-serif italic — A-Z, a-z
    R(0x1D608, 0x41, false, true) ||
    R(0x1D622, 0x61, false, true) ||
    // Sans-serif bold-italic — A-Z, a-z
    R(0x1D63C, 0x41, true,  true) ||
    R(0x1D656, 0x61, true,  true) ||
    // Monospace — A-Z, a-z, 0-9 (already monospace visually, no styling hint)
    R(0x1D670, 0x41, false, false) ||
    R(0x1D68A, 0x61, false, false) ||
    R(0x1D7F6, 0x30, false, false) ||
    null
  );
}

// Kept for backwards compat with older callers — the top of the file.
function normalizeMathBold(s) {
  if (!s) return "";
  let out = "";
  for (const ch of s) {
    const dec = decodeMathAlphanumeric(ch.codePointAt(0));
    out += dec ? dec.ch : ch;
  }
  return out;
}

function isMathBoldCodepoint(cp) {
  const dec = decodeMathAlphanumeric(cp);
  return !!(dec && dec.bold);
}

// Split a raw text string into consecutive runs, each labelled with the
// combined styling implied by its characters:
//   ""   = plain
//   "b"  = bold        → wrap in <strong>
//   "i"  = italic      → wrap in <em>
//   "bi" = bold+italic → wrap in <strong><em>
// The run's own text is already normalised to ASCII so JetBrains Mono
// (which covers ASCII) renders everything in the article font instead
// of falling back to a system serif for the styled Unicode blocks.
function splitStyledRuns(text) {
  if (!text) return [];
  const keyFor = (b, i) => (b && i ? "bi" : b ? "b" : i ? "i" : "");
  const runs = [];
  let buf = "";
  let mode = null;
  for (const ch of text) {
    const dec = decodeMathAlphanumeric(ch.codePointAt(0));
    const chKey = dec ? keyFor(dec.bold, dec.italic) : "";
    const chText = dec ? dec.ch : ch;
    // Whitespace and punctuation stay with the surrounding run so we
    // don't fragment "𝘛𝘩𝘦 𝘉𝘰𝘥𝘺" into 3 <em>s separated by empty runs.
    const isNeutral = !dec && /[\s\-–—:.,'"()©®™\[\]{}!?;/·]/.test(ch);
    const useKey = isNeutral && mode !== null ? mode : chKey;
    if (mode === null) mode = useKey;
    if (useKey !== mode) {
      runs.push({ text: buf, key: mode });
      buf = "";
      mode = useKey;
    }
    buf += chText;
  }
  if (buf) runs.push({ text: buf, key: mode || "" });
  return runs;
}

// A paragraph is treated as a CHAPTER HEADING when its plain text (a) is
// short, and (b) contains at least one Mathematical Bold character while
// every other character is either math-bold, whitespace, or common
// punctuation. This matches the way chapter titles are actually stored in
// existing TipTap documents (e.g. "𝐓𝐡𝐞 𝐂𝐨𝐫𝐩𝐨𝐫𝐞𝐚𝐥 𝐀𝐫𝐜𝐡𝐢𝐯𝐞 𝐨𝐟 𝐏𝐚𝐢𝐧").
function isUnicodeBoldHeading(node) {
  if (!node || node.type !== "paragraph") return false;
  const text = collectText(node).trim();
  if (!text || text.length > 140) return false;
  let hasMathBold = false;
  for (const ch of text) {
    const cp = ch.codePointAt(0);
    const isBold = (cp >= 0x1D400 && cp <= 0x1D433) || (cp >= 0x1D7CE && cp <= 0x1D7D7);
    if (isBold) { hasMathBold = true; continue; }
    // Allow whitespace + common punctuation between bold words.
    if (/[\s\-–—:.,'"()©®™]/.test(ch)) continue;
    return false;
  }
  return hasMathBold;
}

function renderBody(doc) {
  if (!doc || doc.type !== "doc") return renderNode(doc, 0);
  const content = Array.isArray(doc.content) ? doc.content : [];
  // Drop cap goes on the first paragraph with SUBSTANTIAL text (≥ 40 chars),
  // so short label paragraphs like "Foreword" / "Prefazione" appear on their
  // own line without a giant floated capital pulling siblings out of flow.
  let dropCapIdx = -1;
  for (let i = 0; i < content.length; i++) {
    const n = content[i];
    if (n && n.type === "paragraph" && !isUnicodeBoldHeading(n) &&
        collectText(n).trim().length >= 40) {
      dropCapIdx = i;
      break;
    }
  }
  // Any paragraph that sits IMMEDIATELY before the drop-cap paragraph and
  // is short (< 40 chars) is treated as a section-label heading: rendered
  // bold on its own line (e.g. "Foreword"). This mirrors the editorial
  // layout in the reference screenshots where every article opens with a
  // Foreword/Preface heading followed by the drop-cap body.
  const labelIdx = dropCapIdx > 0 && content[dropCapIdx - 1] &&
    content[dropCapIdx - 1].type === "paragraph" &&
    collectText(content[dropCapIdx - 1]).trim().length < 40 &&
    collectText(content[dropCapIdx - 1]).trim().length > 0
    ? dropCapIdx - 1 : -1;
  return (
    <>
      {content.map((n, i) => {
        if (n && n.type === "paragraph") {
          if (isUnicodeBoldHeading(n)) {
            return (
              <h2 key={i} className="chapter-heading">
                {normalizeMathBold(collectText(n))}
              </h2>
            );
          }
          if (i === labelIdx) {
            return <h3 key={i} className="section-label">{renderChildren(n.content)}</h3>;
          }
          const cls = i === dropCapIdx ? "drop-cap" : undefined;
          return <p key={i} className={cls}>{renderChildren(n.content)}</p>;
        }
        return renderNode(n, i);
      })}
    </>
  );
}

export default function ArticleRenderer({ fields, publishedAt, dataTheme }) {
  if (!fields) return null;
  return (
    <div className="article-surface" data-theme={dataTheme || undefined} data-testid="article-renderer">
      <article className="article-container">
        <header className="article-masthead">
          <div className="series" data-testid="article-series">{fields.series_name || "Self Doubling Growing Circle"}</div>
          <div className="date" data-testid="article-date">{fmtDate(publishedAt)}</div>
        </header>
        <h1 className="article-title" data-testid="article-title">{fields.title}</h1>
        {fields.subtitle ? <p className="article-subtitle" data-testid="article-subtitle">{fields.subtitle}</p> : null}
        <div className="article-author">
          <div className="name" data-testid="article-author-name">{fields.author_name || "Maria Gioia Mosca"}</div>
          {fields.author_credentials ? <div className="credentials">{fields.author_credentials}</div> : null}
        </div>
        {fields.opening_note && (typeof fields.opening_note === "string"
          ? <div className="article-opening-note" data-testid="article-opening-note">{fields.opening_note}</div>
          : (fields.opening_note.type === "doc" && Array.isArray(fields.opening_note.content) && fields.opening_note.content.some(n => (n.content || []).length > 0))
            ? <div className="article-opening-note" data-testid="article-opening-note">{renderBody(fields.opening_note)}</div>
            : null
        )}
        {fields.cover_media_id ? (
          <img
            className="article-cover"
            src={mediaUrl(fields.cover_media_id)}
            alt={fields.cover_decorative ? "" : (fields.cover_alt_text || "")}
            data-testid="article-cover"
          />
        ) : null}
        <div className="article-body" data-testid="article-body">{renderBody(fields.body)}</div>
        {fields.author_note_or_biography ? (
          <aside className="article-bio" data-testid="article-bio">
            <div className="label">About the author</div>
            <div>{fields.author_note_or_biography}</div>
          </aside>
        ) : null}
        {fields.copyright_note ? (
          <div className="article-copyright" data-testid="article-copyright">{fields.copyright_note}</div>
        ) : null}
      </article>
    </div>
  );
}
